import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, Check, Loader2, RefreshCw, UserRound } from 'lucide-react';
import Modal from '../../components/modal/Modal';
import {
    getSalesManagers,
    patchSalesManager,
} from '../../api/membershipPayments';
import { getItemWithExpiration } from '../../lib/utils';
import type {
    MembershipPaymentData,
    SalesManager,
} from '../../services/useMembershipPaymentsQueryService';
import { invalidateMembershipPaymentRelatedQueries } from '../../services/useMembershipPaymentsMutationService';
import { isSameSalesManager, resolveSalesRecordId } from './salesRecordDetail';
import {
    prepareSalesManagerRetry,
    resolveSalesManagerCorrectionFailure,
    SalesManagerCorrectionFailure,
    toSalesManagerIdentity,
} from './salesManagerCorrection';

export type SalesManagerCorrectionStep = 'select' | 'confirm' | 'success' | 'error';
export type SalesManagerCorrectionConfirmationMode = 'initial' | 'retry' | 'finish';

const roleLabel = (role?: string) => (role === 'coach' ? '강사' : role === 'etc' ? '기타 직원' : '직원');

type ContentProps = {
    currentManager: SalesManager;
    managers: SalesManager[];
    selectedManager?: SalesManager;
    step: SalesManagerCorrectionStep;
    confirmationMode?: SalesManagerCorrectionConfirmationMode;
    selectionNotice?: string;
    isManagersLoading?: boolean;
    isManagersError?: boolean;
    isSubmitting?: boolean;
    successManager?: SalesManager;
    failure?: SalesManagerCorrectionFailure;
    onSelect: (manager: SalesManager) => void;
    onNext: () => void;
    onBack: () => void;
    onSubmit: () => void;
    onRetry: () => void;
    onRefresh: () => void;
    onClose: () => void;
};

/** 미리보기 하니스에서도 실제 운영 UI를 그대로 렌더할 수 있는 표현 컴포넌트. */
export const SalesManagerCorrectionContent = ({
    currentManager,
    managers,
    selectedManager,
    step,
    confirmationMode = 'initial',
    selectionNotice,
    isManagersLoading,
    isManagersError,
    isSubmitting,
    successManager,
    failure,
    onSelect,
    onNext,
    onBack,
    onSubmit,
    onRetry,
    onRefresh,
    onClose,
}: ContentProps) => {
    const sameManagerSelected = isSameSalesManager(currentManager, selectedManager);

    return (
        <div className="flex min-h-full flex-col bg-white px-[32px] pb-[28px] pt-[30px]">
            <div className="mb-[24px] pr-[44px]">
                <h2 className="text-[21px] font-bold leading-[30px] text-[#111827]">판매자 수정</h2>
                <p className="mt-[5px] text-[13px] leading-[20px] text-[#6B7280]">
                    결제 행을 기준으로 매출 귀속을 바로잡아요.
                </p>
            </div>

            {step === 'select' && (
                <>
                    {selectionNotice && (
                        <div className="mb-[14px] rounded-[10px] border border-solid border-[#BFDBFE] bg-[#EFF6FF] px-[14px] py-[11px] text-[13px] leading-[18px] text-[#1E40AF]">
                            {selectionNotice}
                        </div>
                    )}
                    <div className="mb-[18px] flex items-center gap-[10px] rounded-[11px] bg-[#F8FAFC] px-[14px] py-[12px]">
                        <div className="text-[13px] text-[#6B7280]">현재 판매자</div>
                        <div className="text-[14px] font-bold text-[#1F2937]">{currentManager.userName || '미확인'}</div>
                        <ArrowRight size={15} color="#9CA3AF" />
                        <div className="text-[13px] text-[#6B7280]">새 판매자를 선택해 주세요</div>
                    </div>

                    <div className="mb-[8px] text-[13px] font-semibold text-[#374151]">판매 담당자</div>
                    <div className="max-h-[270px] min-h-[154px] overflow-y-auto rounded-[12px] border border-solid border-[#E5E7EB] p-[8px]">
                        {isManagersLoading ? (
                            <div className="flex h-[136px] items-center justify-center gap-[8px] text-[13px] text-[#6B7280]">
                                <Loader2 className="animate-spin" size={17} /> 판매자를 불러오고 있어요.
                            </div>
                        ) : isManagersError ? (
                            <div className="flex h-[136px] items-center justify-center text-[13px] text-[#B91C1C]">
                                판매자 목록을 불러오지 못했어요.
                            </div>
                        ) : managers.length === 0 ? (
                            <div className="flex h-[136px] items-center justify-center text-[13px] text-[#6B7280]">
                                선택할 수 있는 재직 판매자가 없어요.
                            </div>
                        ) : (
                            managers.map((manager) => {
                                const selected = isSameSalesManager(manager, selectedManager);
                                const current = isSameSalesManager(manager, currentManager);
                                return (
                                    <button
                                        key={`${manager.role}-${manager.userId}`}
                                        type="button"
                                        onClick={() => onSelect(manager)}
                                        className={`mb-[4px] flex w-full items-center justify-between rounded-[9px] border border-solid px-[13px] py-[11px] text-left last:mb-0 ${
                                            selected
                                                ? 'border-[#818CF8] bg-[#EEF2FF]'
                                                : 'border-transparent bg-white hover:bg-[#F9FAFB]'
                                        }`}
                                        style={{ borderColor: selected ? '#818CF8' : 'transparent' }}
                                    >
                                        <span className="flex items-center gap-[9px]">
                                            <span className={`flex h-[28px] w-[28px] items-center justify-center rounded-full ${selected ? 'bg-[#C7D2FE]' : 'bg-[#F3F4F6]'}`}>
                                                <UserRound size={15} color={selected ? '#4338CA' : '#6B7280'} />
                                            </span>
                                            <span>
                                                <span className="block text-[14px] font-semibold text-[#1F2937]">{manager.userName}</span>
                                                <span className="block text-[13px] text-[#6B7280]">{roleLabel(manager.role)}</span>
                                            </span>
                                        </span>
                                        <span className="flex items-center gap-[8px]">
                                            {current && <span className="text-[13px] text-[#6B7280]">현재</span>}
                                            <span className={`flex h-[18px] w-[18px] items-center justify-center rounded-full border ${selected ? 'border-[#4F46E5] bg-[#4F46E5]' : 'border-[#D1D5DB]'}`}>
                                                {selected && <Check size={12} color="white" strokeWidth={3} />}
                                            </span>
                                        </span>
                                    </button>
                                );
                            })
                        )}
                    </div>
                    {sameManagerSelected && (
                        <p className="mt-[8px] text-[13px] text-[#B45309]">현재 판매자와 같은 판매자는 선택할 수 없어요.</p>
                    )}

                    <div className="mt-auto flex justify-end gap-[8px] pt-[22px]">
                        <button type="button" onClick={onClose} className="h-[42px] rounded-[8px] border border-solid border-[#D1D5DB] px-[18px] text-[14px] font-semibold text-[#4B5563]">
                            취소
                        </button>
                        <button
                            type="button"
                            disabled={!selectedManager || sameManagerSelected || isManagersLoading || isManagersError}
                            onClick={onNext}
                            className="h-[42px] rounded-[8px] bg-[#4F46E5] px-[22px] text-[14px] font-bold text-white hover:bg-[#4338CA] disabled:cursor-not-allowed disabled:bg-[#D1D5DB]"
                        >
                            다음
                        </button>
                    </div>
                </>
            )}

            {step === 'confirm' && selectedManager && (
                <>
                    {confirmationMode !== 'initial' && (
                        <div className="mb-[14px] rounded-[10px] border border-solid border-[#BFDBFE] bg-[#EFF6FF] px-[14px] py-[11px] text-left">
                            <div className="text-[13px] font-bold text-[#1E40AF]">
                                {confirmationMode === 'finish'
                                    ? '중간에 멈춘 변경을 마저 맞춰요'
                                    : '최신 판매자로 다시 확인해요'}
                            </div>
                            <div className="mt-[3px] text-[13px] leading-[18px] text-[#3B82F6]">
                                {confirmationMode === 'finish'
                                    ? '목록에는 이미 새 판매자가 보여요. 연결된 매출까지 같은 판매자로 맞추는 요청을 다시 확인해 주세요.'
                                    : '매출 목록을 새로 불러왔어요. 아래 현재 값을 기준으로 다시 눌러야 변경돼요.'}
                            </div>
                        </div>
                    )}
                    <div className="mb-[18px] flex items-center justify-center gap-[14px] rounded-[12px] bg-[#F8FAFC] px-[18px] py-[16px]">
                        <div className="text-center">
                            <div className="text-[13px] text-[#6B7280]">현재</div>
                            <div className="mt-[3px] text-[15px] font-bold text-[#374151]">{currentManager.userName || '미확인'}</div>
                        </div>
                        <ArrowRight size={18} color="#6366F1" />
                        <div className="text-center">
                            <div className="text-[13px] text-[#6B7280]">변경</div>
                            <div className="mt-[3px] text-[15px] font-bold text-[#4338CA]">{selectedManager.userName}</div>
                        </div>
                    </div>

                    <div className="rounded-[12px] border border-solid border-[#FED7AA] bg-[#FFF7ED] px-[16px] py-[14px]">
                        <div className="mb-[9px] flex items-center gap-[7px] text-[13px] font-bold text-[#9A3412]">
                            <AlertTriangle size={16} /> 바뀌는 내용을 확인해 주세요
                        </div>
                        <ul className="m-0 space-y-[7px] pl-[18px] text-[13px] leading-[18px] text-[#9A3412]">
                            <li>같은 수강권의 분할 결제·업그레이드·환불 기록도 함께 바뀌어요.</li>
                            <li>
                                이미 마감된 달의 급여·수업료 정산 금액도 다시 조회하면 달라질 수 있어요. 요율 구간이 바뀌면 그 강사의 다른 수강권 수업료도 달라질 수 있어요.
                            </li>
                            <li>팀 매출도 새 판매자의 팀으로 옮겨가요.</li>
                        </ul>
                    </div>

                    <div className="mt-auto flex justify-end gap-[8px] pt-[22px]">
                        <button type="button" disabled={isSubmitting} onClick={onBack} className="h-[42px] rounded-[8px] border border-solid border-[#D1D5DB] px-[18px] text-[14px] font-semibold text-[#4B5563] disabled:opacity-50">
                            이전
                        </button>
                        <button type="button" disabled={isSubmitting} onClick={onSubmit} className="flex h-[42px] min-w-[112px] items-center justify-center gap-[7px] rounded-[8px] bg-[#4F46E5] px-[20px] text-[14px] font-bold text-white hover:bg-[#4338CA] disabled:bg-[#A5B4FC]">
                            {isSubmitting && <Loader2 className="animate-spin" size={16} />}
                            {isSubmitting
                                ? '바꾸는 중'
                                : confirmationMode === 'finish'
                                  ? '변경 마저 맞추기'
                                  : '판매자 바꾸기'}
                        </button>
                    </div>
                </>
            )}

            {step === 'success' && successManager && (
                <div className="flex flex-1 flex-col items-center justify-center text-center">
                    <div className="mb-[18px] flex h-[54px] w-[54px] items-center justify-center rounded-full bg-[#DCFCE7]">
                        <Check size={28} color="#16A34A" strokeWidth={2.5} />
                    </div>
                    <h3 className="text-[19px] font-bold text-[#111827]">판매자를 수정했어요</h3>
                    <p className="mt-[8px] text-[14px] leading-[21px] text-[#4B5563]">
                        이 결제와 연결된 매출이 <strong>{successManager.userName}</strong>님에게 반영됐어요.
                    </p>
                    <button type="button" onClick={onClose} className="mt-[28px] h-[42px] rounded-[8px] bg-[#4F46E5] px-[30px] text-[14px] font-bold text-white">
                        완료
                    </button>
                </div>
            )}

            {step === 'error' && failure && (
                <div className="flex flex-1 flex-col items-center justify-center text-center">
                    <div className="mb-[18px] flex h-[54px] w-[54px] items-center justify-center rounded-full bg-[#FEF2F2]">
                        <AlertTriangle size={27} color="#DC2626" />
                    </div>
                    <h3 className="text-[19px] font-bold text-[#111827]">{failure.title}</h3>
                    <p className="mt-[8px] max-w-[410px] whitespace-pre-line text-[13px] leading-[20px] text-[#6B7280]">{failure.message}</p>
                    <div className="mt-[26px] flex gap-[8px]">
                        {failure.action === 'retry' && (
                            <button type="button" disabled={isSubmitting} onClick={onRetry} className="flex h-[42px] items-center gap-[7px] rounded-[8px] bg-[#4F46E5] px-[20px] text-[14px] font-bold text-white disabled:bg-[#A5B4FC]">
                                <RefreshCw className={isSubmitting ? 'animate-spin' : ''} size={15} /> 다시 시도
                            </button>
                        )}
                        {failure.action === 'refresh' && (
                            <button type="button" disabled={isSubmitting} onClick={onRefresh} className="flex h-[42px] items-center gap-[7px] rounded-[8px] bg-[#4F46E5] px-[20px] text-[14px] font-bold text-white disabled:bg-[#A5B4FC]">
                                <RefreshCw className={isSubmitting ? 'animate-spin' : ''} size={15} /> 목록 새로고침
                            </button>
                        )}
                        {failure.action === 'dismiss' && (
                            <button type="button" onClick={onClose} className="h-[42px] rounded-[8px] bg-[#4F46E5] px-[24px] text-[14px] font-bold text-white">
                                확인
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

type Props = {
    record: MembershipPaymentData;
    onClose: () => void;
    onUpdated: (salesManager: SalesManager) => void;
    onRequestListRefresh: () => void;
    onRefetchSalesRecord: () => Promise<MembershipPaymentData>;
};

type SalesManagerMutationVariables = {
    manager: SalesManager;
    expectedManager: SalesManager;
};

const SalesManagerCorrectionModal = ({
    record,
    onClose,
    onUpdated,
    onRequestListRefresh,
    onRefetchSalesRecord,
}: Props) => {
    const queryClient = useQueryClient();
    const authorization = getItemWithExpiration('authorization');
    const centerId = getItemWithExpiration('authObject')?.centerId;
    const [step, setStep] = useState<SalesManagerCorrectionStep>('select');
    const [currentManager, setCurrentManager] = useState(record.salesManager);
    const [selectedManager, setSelectedManager] = useState<SalesManager>();
    const [submittedManager, setSubmittedManager] = useState<SalesManager>();
    const [submittedExpectedManager, setSubmittedExpectedManager] = useState<SalesManager>();
    const [successManager, setSuccessManager] = useState<SalesManager>();
    const [failure, setFailure] = useState<SalesManagerCorrectionFailure>();
    const [confirmationMode, setConfirmationMode] = useState<SalesManagerCorrectionConfirmationMode>('initial');
    const [selectionNotice, setSelectionNotice] = useState<string>();
    const [isPreparingRetry, setIsPreparingRetry] = useState(false);

    const managersQuery = useQuery({
        queryKey: ['getSalesManagersQuery', centerId],
        queryFn: () => getSalesManagers(centerId, authorization),
        enabled: !!authorization && !!centerId,
        suspense: false,
        retry: false,
    });

    const managers = useMemo<SalesManager[]>(() => {
        const data = (managersQuery.data as any)?.data?.salesManagers;
        return Array.isArray(data) ? data.filter((manager: SalesManager) => !manager.isDeleted) : [];
    }, [managersQuery.data]);

    const mutation = useMutation(
        ['patchSalesManagerMutation'],
        ({ manager, expectedManager }: SalesManagerMutationVariables) => {
            const membershipPaymentId = resolveSalesRecordId(record);
            if (!membershipPaymentId) {
                return Promise.resolve({ status: 0, data: { errorCode: 'MEMBERSHIP_PAYMENT_ID_MISSING' } });
            }
            const expectedSalesManager = expectedManager?.userId?.trim()
                ? toSalesManagerIdentity(expectedManager)
                : null;
            return patchSalesManager({
                membershipPaymentId,
                salesManager: { role: manager.role, userId: manager.userId },
                expectedSalesManager,
                accessToken: authorization,
            });
        },
        {
            onSuccess: (result: any, { manager }) => {
                if (result?.status === 200) {
                    const responseManager = result?.data?.salesManager;
                    const updatedManager: SalesManager = {
                        role: responseManager?.role ?? manager.role,
                        userId: responseManager?.userId ?? manager.userId,
                        userName: responseManager?.userName ?? manager.userName,
                    };
                    setSuccessManager(updatedManager);
                    setStep('success');
                    onUpdated(updatedManager);
                    return;
                }
                setFailure(resolveSalesManagerCorrectionFailure(result));
                setStep('error');
            },
            onError: () => {
                setFailure(resolveSalesManagerCorrectionFailure(null));
                setStep('error');
            },
            onSettled: () => {
                void invalidateMembershipPaymentRelatedQueries(queryClient);
            },
        },
    );

    // 재조회 중에는 닫을 수 있으므로, 닫힌 뒤 늦게 끝난 재조회가 상태를 바꾸거나 부모 상세 모달을
    // 닫지 않게 한다(그 사이 새로 연 정정 모달·진행 중 PATCH 까지 함께 닫혀 버린다).
    const isMountedRef = useRef(true);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const submit = (manager?: SalesManager) => {
        if (!manager || (confirmationMode !== 'finish' && isSameSalesManager(currentManager, manager))) return;
        setSubmittedManager(manager);
        setSubmittedExpectedManager(currentManager);
        mutation.mutate({ manager, expectedManager: currentManager });
    };

    const prepareRetryConfirmation = async () => {
        if (!submittedManager || !submittedExpectedManager || isPreparingRetry) return;

        setIsPreparingRetry(true);
        try {
            const resolution = await prepareSalesManagerRetry(
                submittedExpectedManager,
                submittedManager,
                onRefetchSalesRecord,
            );
            if (!isMountedRef.current) return;
            setCurrentManager(resolution.currentManager);
            setFailure(undefined);
            mutation.reset();

            if (resolution.kind === 'reselect') {
                setSelectedManager(undefined);
                setSubmittedManager(undefined);
                setSubmittedExpectedManager(undefined);
                setConfirmationMode('initial');
                setSelectionNotice(
                    `다른 곳에서 판매자가 ${resolution.currentManager.userName || '다른 판매자'}님으로 바뀌었어요. 새 판매자를 처음부터 골라 주세요.`,
                );
                setStep('select');
                return;
            }

            setSelectedManager(submittedManager);
            setSelectionNotice(undefined);
            setConfirmationMode(resolution.kind === 'finish' ? 'finish' : 'retry');
            setStep('confirm');
        } catch {
            if (!isMountedRef.current) return;
            setFailure({
                title: '최신 매출을 불러오지 못했어요',
                message: '현재 판매자를 확인하지 못해 변경 요청을 보내지 않았어요. 다시 시도해 주세요.',
                action: 'retry',
            });
            setStep('error');
        } finally {
            if (isMountedRef.current) setIsPreparingRetry(false);
        }
    };

    const refreshListAfterConflict = async () => {
        if (isPreparingRetry) return;
        setIsPreparingRetry(true);
        try {
            await queryClient.refetchQueries(
                { queryKey: ['getSalesListQuery'], type: 'active' },
                { throwOnError: true },
            );
        } catch {
            // 목록 화면 자체의 조회 실패 UI에서 다시 시도할 수 있으므로 상세 모달은 닫는다.
        } finally {
            if (isMountedRef.current) {
                setIsPreparingRetry(false);
                onRequestListRefresh();
            }
        }
    };

    // 판매자 변경 요청(PATCH)이 나가 있는 동안만 닫지 않는다 — 실패하면 "다시 시도" 복구 동선을 놓친다.
    // PATCH 에는 시간 제한이 있어 반드시 풀린다. 재조회(다시 시도 준비·충돌 후 새로고침)는 쓰기가 없어
    // 닫아도 안전하고, 재조회 요청에는 시간 제한이 없어 여기서 막으면 영영 못 닫을 수 있다.
    // Modal 이 Esc 핸들러를 첫 렌더에만 붙이므로 최신 상태는 ref 로 읽는다.
    const isBusyRef = useRef(false);
    isBusyRef.current = mutation.isLoading;
    const handleClose = useCallback(() => {
        if (isBusyRef.current) return;
        onClose();
    }, [onClose]);

    return (
        <Modal whiteViewWidth={560} whiteViewHeight={590} borderRadius={16} isCloseButton isCloseOutside={false} onClose={handleClose}>
            <SalesManagerCorrectionContent
                currentManager={currentManager}
                managers={managers}
                selectedManager={selectedManager}
                step={step}
                confirmationMode={confirmationMode}
                selectionNotice={selectionNotice}
                isManagersLoading={managersQuery.isLoading || managersQuery.isFetching}
                isManagersError={managersQuery.isError || (!authorization || !centerId)}
                isSubmitting={mutation.isLoading || isPreparingRetry}
                successManager={successManager}
                failure={failure}
                onSelect={(manager) => {
                    setSelectionNotice(undefined);
                    setSelectedManager(manager);
                }}
                onNext={() => {
                    setConfirmationMode('initial');
                    setStep('confirm');
                }}
                onBack={() => {
                    mutation.reset();
                    setFailure(undefined);
                    setConfirmationMode('initial');
                    setStep('select');
                }}
                onSubmit={() => submit(selectedManager)}
                onRetry={() => void prepareRetryConfirmation()}
                onRefresh={() => void refreshListAfterConflict()}
                onClose={onClose}
            />
        </Modal>
    );
};

export default SalesManagerCorrectionModal;
